import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import Dashboard from "../../../Components/Dashboard";
import Denied from "../../../Components/Denied";
import { apiIDW, apiLabs } from "../../../Services/api";
import { store } from "../../../store";
import {
  colors, globalCSS, marketplaceColors,
  page, header, progress, notice, kpis, section, table, modal,
} from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de pedido dos marketplaces
// ─────────────────────────────────────────────────────────────────────────────
// A consulta é feita UM TIPO POR VEZ de propósito: com SkuView=1 a API deixa de
// devolver IDOrderType/TypeOrder, e o CompanyIntegration que sobra não separa
// Shopee de Shopee Rápido nem ML Flex de ML Padrão. Consultando por tipo, o
// rótulo vem da própria consulta e não depende do corpo da resposta.
const MARKETPLACE_TYPES = [
  { id: 2967, label: "Amazon DBA" },
  { id: 4410, label: "Mercado Livre Flex" },
  { id: 4412, label: "Mercado Livre Padrão" },
  { id: 4041, label: "Shopee" },
  { id: 5440, label: "Shopee Rápido" },
  { id: 4293, label: "TikTok" },
];

// A API devolve no máximo 2000 registros, ordenados por IDOrder DECRESCENTE, e
// pagina por LastID (o último IDOrder do lote) — não por Page.
const IDW_PAGE_SIZE = 2000;
const IDW_MAX_PAGES = 40; // trava de segurança: 80 mil pedidos por tipo
const REQUEST_DELAY_MS = 1000; // respeita o rate limit da IDWorks

// Quantos SKUs distintos agrupar antes de cair no balde "6 ou mais"
const SKU_BUCKET_LIMIT = 5;
const RANKING_SIZE = 10;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Miniatura do produto. Imagem quebrada cai no placeholder — o alinhamento
// das linhas não pode depender da URL responder.
function renderSkuThumb(item) {
  if (!item.imageUrl) return <span style={table.skuThumbEmpty} />;
  return (
    <img
      src={item.imageUrl}
      alt=""
      loading="lazy"
      style={table.skuThumb}
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatadores
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");

const formatDecimal = (value, digits = 1) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

// Primeiro e último dia do mês corrente, em horário local
function currentMonthRange() {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const asISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: asISO(firstDay), to: asISO(lastDay) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Agregação
// ─────────────────────────────────────────────────────────────────────────────
// `Items` traz UMA LINHA POR SKU, com Quantity. Então:
//   SKUs distintos do pedido = quantidade de linhas (deduplicadas por IDSku)
//   unidades do pedido       = soma das Quantity
function summarizeOrders(ordersByType) {
  const byType = [];
  const skuBuckets = new Map();         // nº de SKUs → pedidos
  const singleSkuRanking = new Map();   // pedido de 1 SKU, qualquer quantidade
  const singleOrderRanking = new Map(); // pedido de 1 SKU x 1 unidade
  const singleUnitRanking = new Map();  // linha com Quantity 1, em qualquer pedido

  let totalOrders = 0;
  let totalValue = 0;
  let totalUnits = 0;
  let ordersWithoutItems = 0;

  const bumpRanking = (map, item, orderValue, units) => {
    const key = item.IDSku;
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: item.SkuNameOnly || item.SkuName || `SKU ${key}`,
        imageUrl: item.MainImageURL || null,
        orders: 0,
        value: 0,
        units: 0,
      });
    }
    const entry = map.get(key);
    entry.orders += 1;
    entry.value += orderValue;
    entry.units += units || 0;
    return entry;
  };

  MARKETPLACE_TYPES.forEach((type) => {
    const orders = ordersByType[type.id] || [];
    let typeValue = 0;
    let typeUnits = 0;
    let typeSkuLines = 0;

    orders.forEach((order) => {
      const orderValue = parseFloat(order.ValueOrder) || 0;
      const rawItems = Array.isArray(order.Items) ? order.Items : [];

      // Dedup por IDSku: a API já devolve uma linha por SKU, mas apareceu
      // pedido com a mesma SKU repetida — somamos as quantidades nesse caso.
      const itemsBySku = new Map();
      rawItems.forEach((item) => {
        const existing = itemsBySku.get(item.IDSku);
        const quantity = Number(item.Quantity) || 0;
        if (existing) existing.quantity += quantity;
        else itemsBySku.set(item.IDSku, { ...item, quantity });
      });
      const items = [...itemsBySku.values()];

      const skuCount = items.length;
      const unitCount = items.reduce((sum, item) => sum + item.quantity, 0);

      typeValue += orderValue;
      typeUnits += unitCount;
      typeSkuLines += skuCount;

      totalValue += orderValue;
      totalUnits += unitCount;
      totalOrders += 1;

      if (skuCount === 0) {
        ordersWithoutItems += 1;
      } else {
        const bucketKey = Math.min(skuCount, SKU_BUCKET_LIMIT + 1);
        skuBuckets.set(bucketKey, (skuBuckets.get(bucketKey) || 0) + 1);
      }

      if (skuCount === 1) {
        // Ranking 1: pedido de um SKU só, seja qual for a quantidade
        bumpRanking(singleSkuRanking, items[0], orderValue, items[0].quantity);

        // Ranking 2: o pedido unitário puro — um SKU, uma unidade
        if (items[0].quantity === 1) {
          bumpRanking(singleOrderRanking, items[0], orderValue, 1);
        }
      }

      // Ranking 3: SKU levado avulso, mesmo com outros SKUs no pedido
      items.forEach((item) => {
        if (item.quantity === 1) bumpRanking(singleUnitRanking, item, 0, 1);
      });
    });

    byType.push({
      ...type,
      orders: orders.length,
      value: typeValue,
      units: typeUnits,
      skuLines: typeSkuLines,
      averageTicket: orders.length ? typeValue / orders.length : 0,
      averageUnits: orders.length ? typeUnits / orders.length : 0,
    });
  });

  const bucketRows = [...skuBuckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([skuCount, orders]) => ({
      key: skuCount,
      label: skuCount > SKU_BUCKET_LIMIT
        ? `${SKU_BUCKET_LIMIT + 1} ou mais SKUs`
        : `${skuCount} SKU${skuCount > 1 ? "s" : ""}`,
      orders,
      percentage: totalOrders ? (orders / totalOrders) * 100 : 0,
    }));

  // Lista COMPLETA ordenada — o card mostra só o topo, mas o "ver mais" e a
  // exportação usam tudo.
  const rankedOf = (map) =>
    [...map.values()].sort((a, b) => b.orders - a.orders);

  const sumOrders = (map) =>
    [...map.values()].reduce((total, entry) => total + entry.orders, 0);

  return {
    byType: byType.sort((a, b) => b.orders - a.orders),
    bucketRows,
    singleSkuRanked: rankedOf(singleSkuRanking),
    singleOrderRanked: rankedOf(singleOrderRanking),
    singleUnitRanked: rankedOf(singleUnitRanking),
    singleSkuCount: sumOrders(singleSkuRanking),
    totalOrders,
    totalValue,
    totalUnits,
    ordersWithoutItems,
    averageTicket: totalOrders ? totalValue / totalOrders : 0,
    averageUnits: totalOrders ? totalUnits / totalOrders : 0,
    singleOrderCount: sumOrders(singleOrderRanking),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────────────────────
export default function MarketplacesDashboard(props) {
  const { location: { idPage } } = props;
  const [auth, setAuth] = useState(null);

  async function verifyAuth() {
    try {
      const { profile } = store.getState().user;
      const res = await apiLabs.post("valid_key", { id: profile.id, menu_id: idPage });
      if (res.data.message === "User not authorized") return setAuth("denied");
      return setAuth("authorized");
    } catch (error) {
      return setAuth("denied");
    }
  }

  React.useEffect(() => {
    verifyAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc fecha o ranking completo
  React.useEffect(() => {
    function handleKey(event) {
      if (event.key === "Escape") setRankingModal(null);
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const defaultRange = useMemo(currentMonthRange, []);
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState({ label: "", done: 0, total: 0 });
  const [errorMessage, setErrorMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [loadedRange, setLoadedRange] = useState(null);
  // Card cujo ranking completo está aberto no modal (null = fechado)
  const [rankingModal, setRankingModal] = useState(null);

  /**
   * Busca todos os pedidos de UM tipo no período, seguindo a paginação por
   * LastID até vir uma página incompleta.
   */
  async function fetchOrdersOfType(type, isFirstRequest) {
    const collected = [];
    let lastId = null;

    for (let pageIndex = 0; pageIndex < IDW_MAX_PAGES; pageIndex += 1) {
      // Delay entre requisições pra não estourar o rate limit da IDWorks
      if (!(isFirstRequest && pageIndex === 0)) await delay(REQUEST_DELAY_MS);

      const params = {
        InvoiceDateFrom: dateFrom,
        InvoiceDateTo: dateTo,
        IDOrderType: type.id,
        Budget: 0,
        SkuView: 1,
      };
      if (lastId != null) params.LastID = lastId;

      const response = await apiIDW.get("/orders", { params });

      // A API às vezes responde HTML (erro de gateway) com status 200. Tratar
      // isso como "página vazia" encerraria o laço e truncaria os dados em
      // silêncio — melhor falhar alto e deixar o operador recarregar.
      if (!Array.isArray(response.data)) {
        throw new Error(
          `resposta inesperada da API em ${type.label} (página ${pageIndex + 1})`
        );
      }

      const rows = response.data;
      collected.push(...rows);

      if (rows.length < IDW_PAGE_SIZE) break;
      lastId = rows[rows.length - 1].IDOrder;
    }

    return collected;
  }

  async function loadDashboard() {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) {
      setErrorMessage("A data inicial não pode ser maior que a final.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSummary(null);

    try {
      const ordersByType = {};
      for (let index = 0; index < MARKETPLACE_TYPES.length; index += 1) {
        const type = MARKETPLACE_TYPES[index];
        setLoadingStep({ label: type.label, done: index, total: MARKETPLACE_TYPES.length });
        ordersByType[type.id] = await fetchOrdersOfType(type, index === 0);
      }
      setLoadingStep({ label: "", done: MARKETPLACE_TYPES.length, total: MARKETPLACE_TYPES.length });
      setSummary(summarizeOrders(ordersByType));
      setLoadedRange({ from: dateFrom, to: dateTo });
    } catch (error) {
      console.error("Marketplaces — erro ao buscar pedidos:", error);
      const message = error?.response?.data?.error || error?.response?.data?.message || error.message;
      setErrorMessage(`Não foi possível carregar os pedidos: ${message}`);
    } finally {
      setLoading(false);
    }
  }

  function resetFilters() {
    const range = currentMonthRange();
    setDateFrom(range.from);
    setDateTo(range.to);
  }

  if (auth === null) return <Dashboard><div style={page} /></Dashboard>;
  if (auth === "denied") return <Denied />;

  const maxTypeOrders = summary
    ? Math.max(1, ...summary.byType.map((t) => t.orders))
    : 1;

  // ── Rankings: card mostra o topo, modal e Excel usam a lista completa ─────
  const rankingCards = summary ? [
    {
      key: "singleSku",
      title: "Top SKUs · pedido com 1 SKU",
      subtitle: "um único SKU no pedido, qualquer quantidade",
      rows: summary.singleSkuRanked,
      emptyText: "Nenhum pedido de SKU único no período.",
      showUnits: true,
      showValue: true,
    },
    {
      key: "singleOrder",
      title: "Top SKUs · pedido de 1 SKU × 1 unidade",
      subtitle: "o pedido unitário puro — candidatos a fluxo simplificado",
      rows: summary.singleOrderRanked,
      emptyText: "Nenhum pedido unitário no período.",
      showUnits: false,
      showValue: true,
    },
    {
      key: "singleUnit",
      title: "Top SKUs · levados em 1 unidade",
      subtitle: "linhas com quantidade 1, mesmo em pedidos com outros SKUs",
      rows: summary.singleUnitRanked,
      emptyText: "Nenhum item unitário no período.",
      showUnits: false,
      showValue: false,
    },
  ] : [];

  const periodSuffix = loadedRange
    ? `${loadedRange.from}_a_${loadedRange.to}`
    : "periodo";

  function exportRankingToExcel(card) {
    const rows = card.rows.map((item, index) => {
      const row = {
        "#": index + 1,
        SKU: item.key,
        Produto: item.name,
        Pedidos: item.orders,
      };
      if (card.showUnits) row.Unidades = item.units;
      if (card.showValue) row.Valor = Number(item.value.toFixed(2));
      row["Imagem"] = item.imageUrl || "";
      return row;
    });

    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    // Nome de aba no Excel tem limite de 31 caracteres
    XLSX.utils.book_append_sheet(book, sheet, card.title.slice(0, 31));
    XLSX.writeFile(book, `marketplaces_${card.key}_${periodSuffix}.xlsx`);
  }

  function exportTypesToExcel() {
    const rows = summary.byType.map((type) => ({
      Marketplace: type.label,
      Pedidos: type.orders,
      Valor: Number(type.value.toFixed(2)),
      "Ticket medio": Number(type.averageTicket.toFixed(2)),
      Unidades: type.units,
      "Unidades por pedido": Number(type.averageUnits.toFixed(2)),
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Por tipo de pedido");
    XLSX.writeFile(book, `marketplaces_tipos_${periodSuffix}.xlsx`);
  }

  function renderRankingRows(card, limit) {
    const visible = limit ? card.rows.slice(0, limit) : card.rows;
    const columnCount = 2 + (card.showUnits ? 1 : 0) + (card.showValue ? 1 : 0);

    if (visible.length === 0) {
      return (
        <tr>
          <td style={{ ...table.td, ...table.tdMuted }} colSpan={columnCount}>
            {card.emptyText}
          </td>
        </tr>
      );
    }

    return visible.map((item, index) => (
      <tr key={item.key}>
        <td style={table.td} title={item.name}>
          <div style={table.skuCell}>
            <span style={table.rank}>{index + 1}</span>
            {renderSkuThumb(item)}
            <span style={table.skuName}>{item.name}</span>
          </div>
        </td>
        <td style={{ ...table.td, ...table.tdRight }}>{formatNumber(item.orders)}</td>
        {card.showUnits && (
          <td style={{ ...table.td, ...table.tdRight }}>{formatNumber(item.units)}</td>
        )}
        {card.showValue && (
          <td style={{ ...table.td, ...table.tdRight }}>{formatCurrency(item.value)}</td>
        )}
      </tr>
    ));
  }

  function renderRankingHead(card) {
    return (
      <tr>
        <th style={table.th}>Produto</th>
        <th style={{ ...table.th, ...table.thRight }}>Pedidos</th>
        {card.showUnits && <th style={{ ...table.th, ...table.thRight }}>Unidades</th>}
        {card.showValue && <th style={{ ...table.th, ...table.thRight }}>Valor</th>}
      </tr>
    );
  }

  function renderRankingCard(card) {
    const hiddenCount = Math.max(0, card.rows.length - RANKING_SIZE);
    return (
      <div key={card.key} style={section.card}>
        <div style={section.cardHead}>
          <div style={{ minWidth: 0 }}>
            <div style={section.title}>{card.title}</div>
            <div style={section.subtitle}>{card.subtitle}</div>
          </div>
          <div style={section.cardActions}>
            {hiddenCount > 0 && (
              <button
                type="button"
                style={section.actionButton}
                onClick={() => setRankingModal(card)}
              >
                Ver mais ({formatNumber(card.rows.length)})
              </button>
            )}
            <button
              type="button"
              style={section.actionButton}
              onClick={() => exportRankingToExcel(card)}
              disabled={card.rows.length === 0}
              title="Baixar a listagem completa em Excel"
            >
              Excel
            </button>
          </div>
        </div>
        <table style={table.base}>
          <thead>{renderRankingHead(card)}</thead>
          <tbody>{renderRankingRows(card, RANKING_SIZE)}</tbody>
        </table>
      </div>
    );
  }

  const formatPeriod = (range) => {
    if (!range) return "";
    const toBR = (iso) => iso.split("-").reverse().join("/");
    return `${toBR(range.from)} a ${toBR(range.to)}`;
  };

  return (
    <Dashboard>
      <style>{globalCSS}</style>
      <div style={page}>
        <div style={header.wrap}>
          <div>
            <h1 style={header.title}>Marketplaces</h1>
            <div style={header.subtitle}>
              Pedidos faturados por período —{" "}
              {MARKETPLACE_TYPES.map((t) => t.label).join(" · ")}
            </div>
          </div>

          <div style={header.filters}>
            <div style={header.field}>
              <span style={header.label}>Faturado de</span>
              <input
                type="date"
                style={header.input}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={loading}
              />
            </div>
            <div style={header.field}>
              <span style={header.label}>até</span>
              <input
                type="date"
                style={header.input}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              type="button"
              style={{ ...header.button, ...(loading ? header.buttonDisabled : {}) }}
              onClick={loadDashboard}
              disabled={loading}
            >
              {loading ? "Carregando…" : "Carregar"}
            </button>
            <button
              type="button"
              style={{ ...header.buttonGhost, ...(loading ? header.buttonDisabled : {}) }}
              onClick={resetFilters}
              disabled={loading}
            >
              Mês atual
            </button>
          </div>
        </div>

        {loading && (
          <div style={progress.wrap}>
            <div style={progress.text}>
              Buscando {loadingStep.label || "pedidos"} — {loadingStep.done} de{" "}
              {loadingStep.total} marketplaces. A API devolve 2000 por vez e há
              1s entre as requisições, então períodos longos demoram um pouco.
            </div>
            <div style={progress.bar}>
              <div
                style={progress.fill(
                  loadingStep.total ? (loadingStep.done / loadingStep.total) * 100 : 0
                )}
              />
            </div>
          </div>
        )}

        {errorMessage && <div style={notice.error}>⚠ {errorMessage}</div>}

        {!loading && !summary && !errorMessage && (
          <div style={notice.empty}>
            Escolha o período de faturamento e clique em <strong>Carregar</strong>.
          </div>
        )}

        {summary && (
          <>
            {/* ── KPIs gerais ────────────────────────────────────────────── */}
            <div style={kpis.grid}>
              <div style={kpis.card}>
                <div style={kpis.label}>Pedidos faturados</div>
                <div style={kpis.value}>{formatNumber(summary.totalOrders)}</div>
                <div style={kpis.hint}>{formatPeriod(loadedRange)}</div>
              </div>
              <div style={kpis.card}>
                <div style={kpis.label}>Valor faturado</div>
                <div style={{ ...kpis.value, ...kpis.valueMoney }}>
                  {formatCurrency(summary.totalValue)}
                </div>
                <div style={kpis.hint}>
                  ticket médio {formatCurrency(summary.averageTicket)}
                </div>
              </div>
              <div style={kpis.card}>
                <div style={kpis.label}>Unidades vendidas</div>
                <div style={kpis.value}>{formatNumber(summary.totalUnits)}</div>
                <div style={kpis.hint}>
                  {formatDecimal(summary.averageUnits)} por pedido
                </div>
              </div>
              <div style={kpis.card}>
                <div style={kpis.label}>Pedidos de 1 SKU × 1 unidade</div>
                <div style={kpis.value}>{formatNumber(summary.singleOrderCount)}</div>
                <div style={kpis.hint}>
                  {formatDecimal(
                    summary.totalOrders
                      ? (summary.singleOrderCount / summary.totalOrders) * 100
                      : 0
                  )}
                  % do total
                </div>
              </div>
            </div>

            {/* ── Por marketplace ────────────────────────────────────────── */}
            <div style={{ ...section.card, marginBottom: 16 }}>
              <div style={section.cardHead}>
                <div>
                  <div style={section.title}>Por tipo de pedido</div>
                  <div style={section.subtitle}>
                    valor e quantidade faturados em cada marketplace
                  </div>
                </div>
                <div style={section.cardActions}>
                  <button
                    type="button"
                    style={section.actionButton}
                    onClick={exportTypesToExcel}
                  >
                    Excel
                  </button>
                </div>
              </div>
              <table style={table.base}>
                <thead>
                  <tr>
                    <th style={table.th}>Marketplace</th>
                    <th style={{ ...table.th, ...table.thRight }}>Pedidos</th>
                    <th style={{ ...table.th, ...table.thRight }}>Valor</th>
                    <th style={{ ...table.th, ...table.thRight }}>Ticket médio</th>
                    <th style={{ ...table.th, ...table.thRight }}>Unidades</th>
                    <th style={{ ...table.th, ...table.thRight }}>Un./pedido</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byType.map((type) => (
                    <tr key={type.id}>
                      <td style={table.td}>
                        <span style={table.typeDot(marketplaceColors[type.id])} />
                        {type.label}
                        <span
                          style={table.shareBar(
                            (type.orders / maxTypeOrders) * 100,
                            marketplaceColors[type.id]
                          )}
                        />
                      </td>
                      <td style={{ ...table.td, ...table.tdRight }}>
                        {formatNumber(type.orders)}
                      </td>
                      <td style={{ ...table.td, ...table.tdRight }}>
                        {formatCurrency(type.value)}
                      </td>
                      <td style={{ ...table.td, ...table.tdRight }}>
                        {formatCurrency(type.averageTicket)}
                      </td>
                      <td style={{ ...table.td, ...table.tdRight }}>
                        {formatNumber(type.units)}
                      </td>
                      <td style={{ ...table.td, ...table.tdRight }}>
                        {formatDecimal(type.averageUnits)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Composição dos pedidos + rankings ──────────────────────── */}
            <div style={section.grid}>
              <div style={section.card}>
                <div style={section.title}>Pedidos por quantidade de SKUs</div>
                <div style={section.subtitle}>
                  SKUs distintos no pedido, não unidades
                </div>
                <table style={table.base}>
                  <thead>
                    <tr>
                      <th style={table.th}>Composição</th>
                      <th style={{ ...table.th, ...table.thRight }}>Pedidos</th>
                      <th style={{ ...table.th, ...table.thRight }}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.bucketRows.map((row) => (
                      <tr key={row.key}>
                        <td style={table.td}>
                          {row.label}
                          <span style={table.shareBar(row.percentage, colors.info)} />
                        </td>
                        <td style={{ ...table.td, ...table.tdRight }}>
                          {formatNumber(row.orders)}
                        </td>
                        <td style={{ ...table.td, ...table.tdRight }}>
                          {formatDecimal(row.percentage)}%
                        </td>
                      </tr>
                    ))}
                    {summary.ordersWithoutItems > 0 && (
                      <tr>
                        <td style={{ ...table.td, ...table.tdMuted }}>
                          sem itens retornados
                        </td>
                        <td style={{ ...table.td, ...table.tdRight }}>
                          {formatNumber(summary.ordersWithoutItems)}
                        </td>
                        <td style={{ ...table.td, ...table.tdRight }}>—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {rankingCards.map((card) => renderRankingCard(card))}
            </div>
          </>
        )}

        {/* Ranking completo — fecha no backdrop, no X ou no Esc */}
        {rankingModal && (
          <div
            style={modal.overlay}
            onClick={() => setRankingModal(null)}
            role="presentation"
          >
            <div style={modal.box} onClick={(event) => event.stopPropagation()}>
              <div style={modal.head}>
                <div style={{ minWidth: 0 }}>
                  <div style={section.title}>{rankingModal.title}</div>
                  <div style={section.subtitle}>
                    {rankingModal.subtitle} · {formatNumber(rankingModal.rows.length)}{" "}
                    SKUs
                  </div>
                </div>
                <div style={section.cardActions}>
                  <button
                    type="button"
                    style={section.actionButton}
                    onClick={() => exportRankingToExcel(rankingModal)}
                  >
                    Excel
                  </button>
                  <button
                    type="button"
                    style={modal.close}
                    onClick={() => setRankingModal(null)}
                    title="Fechar"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div style={modal.body}>
                <table style={table.base}>
                  <thead>{renderRankingHead(rankingModal)}</thead>
                  <tbody>{renderRankingRows(rankingModal, null)}</tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
}
